"use client"

/**
 * 08 — The practical part. Figma 328:13589 / 328:13590 / 435:1473.
 *
 * PLACEHOLDER, not the design (Eugene, 08.09). The section's heading holds
 * its half of the row and stays there; the steps simply ride past it on the
 * right. No cards yet — Eugene wants to see the run of steps on its own
 * before deciding whether they need containers.
 *
 * The measurements are membership's "One membership. Your rhythm.", which is
 * where this site already solved a run of steps with pictures, scaled down —
 * this section has to be a good deal more compact than that one:
 *
 *   rail        1px of neutral-100 down the steps' half, masked at 6%/94%
 *   text        64 from the rail, 12 between the title and the body
 *   marker      8px on the rail, 67 left of the text, ringed by the page
 *   picture     neutral-100 under it, but 360 wide against membership's
 *               594, 3/2 rather than 8/7, radius 16
 *
 * No badges. Membership's coloured pill with the timing in it was only
 * stealing room and adding noise here (Eugene, 08.09).
 *
 * Each step holds still before it leaves: it is a sticky box in a wrapper
 * about half a screen taller than it, so the step rides up, parks for the
 * slack and then the next one pushes it off. Membership does the same with a
 * full-screen box in a 128vh wrapper; this is the compact version of it.
 *
 * It parks on the section heading's own first line: the step's title, the
 * square on the rail and the first line of the h2 all share one horizontal
 * axis — line middles, not baselines (Eugene, 08.09). The heading column
 * centres itself in the screen, so that axis is half the screen less half the
 * heading block plus half a line, which is where the sticky offset comes
 * from; the step starts with its title, so the offset is that less half the
 * title's own line.
 *
 * The type sits a rung under membership's — headline-sm for the step, body-lg
 * beneath it — because at display-sm the steps argued with the section's own
 * 48px heading (Eugene, 08.09).
 *
 * Spacing says what belongs to what: 10 between the title, its line and the
 * picture, against 96 between one step and the next.
 *
 * The tube sits on the blood draw, the second step, where it belongs: 36
 * frames of the Blender render (cap descending and screwing on) drawn onto a
 * canvas and scrubbed as that picture crosses the middle of the screen.
 *
 * The wireframe's numbered list (328:13967) is parked in
 * `HomeV4PracticalSteps` at the foot of the file.
 */

import { useEffect, useRef, useState } from "react"
import NextImage from "next/image"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { ArrowRightIcon, Button } from "@/components/ui/button"
import { blobMediaUrl } from "@/lib/blob-url"
import { cn } from "@/lib/utils"
import { useHomeV4Copy } from "./home-v4-copy"
import { bookingFlowUrl } from "./home-v4-data"

const TUBE = blobMediaUrl("/images/marketing/home-v3/tube")
const TUBE_FRAMES = 36
/**
 * Frame size as uploaded: the tube's alpha bbox over all 36 frames, 1:1 off
 * the 2048² render. `v` busts the 30-day Blob cache on the same paths.
 */
const TUBE_W = 360
const TUBE_H = 1900
const TUBE_V = "2"
/**
 * The tube stands taller than its picture and the picture crops it: cap, neck
 * and the top of the label show, the rest runs out under the bottom edge. Fit
 * the whole thing in and it is 60px wide, at which the cap's screwing stops
 * reading — the same reason the earlier card cropped it (Eugene, 07.09). It
 * is sized against the picture's width rather than its height, so shortening
 * the picture crops more of the tube instead of shrinking it.
 */
const TUBE_FRAC = 0.265
const TUBE_TOP = 24
/**
 * The band the tube turns in: the picture's middle crossing from here to
 * here, as a fraction of the screen. Everything outside is the finished cap.
 * The step parks with its picture at 0.66, so the cap has to finish just
 * above that or it would freeze half screwed on for the whole pause.
 */
const SCRUB_FROM = 0.98
const SCRUB_TO = 0.67
/**
 * Where the two sticky boxes park, as their distance from half the screen:
 * the heading block 105 above it (see the heading's comment), a step's box
 * 76 above: the 50 that put its title on the heading's first line, plus the
 * day label's 16 and the 10 under it, since the label now sits over the
 * title (Eugene, 18.09). The 29 between them is the heading's head start.
 */
const HEAD_TOP = -105
const STEP_TOP = -76

function PracticalStyles() {
  return (
    <style>{`
      .hv3-pr-rail{-webkit-mask-image:linear-gradient(to bottom,transparent 0%,#000 6%,#000 94%,transparent 100%);mask-image:linear-gradient(to bottom,transparent 0%,#000 6%,#000 94%,transparent 100%)}
      .hv3-pr-tube{position:absolute;left:50%;top:${TUBE_TOP}px;translate:-50% 0;width:${TUBE_FRAC * 100}%;aspect-ratio:${TUBE_W} / ${TUBE_H}}
    `}</style>
  )
}

export function HomeV4Practical({ section }: { section: string }) {
  const { PRACTICAL } = useHomeV4Copy()
  const steps = PRACTICAL.steps
  const [active, setActive] = useState(0)
  const stepsRef = useRef<Array<HTMLDivElement | null>>([])
  const shotRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const headRef = useRef<HTMLDivElement>(null)
  const lastRef = useRef<HTMLDivElement>(null)
  const [tail, setTail] = useState(0)

  /**
   * The heading leaves with the last step, not after it. Both are sticky in
   * the same grid row, so each unsticks when the row's bottom reaches its own
   * bottom — and the step's box (title, body, picture) runs well below the
   * heading's, so the square and the step rode up while the heading sat on
   * and looked stuck to the section's foot (Eugene, 09.09). Padding under the
   * heading to the step box's bottom edge makes the two bottoms one.
   */
  useEffect(() => {
    const head = headRef.current
    const last = lastRef.current
    if (!head || !last) return
    const measure = () => {
      if (!window.matchMedia("(min-width: 1024px)").matches) {
        setTail(0)
        return
      }
      const lead = STEP_TOP - HEAD_TOP
      // offsetHeight includes the synchronising padding we set below. Remove
      // it from the measurement so ResizeObserver does not alternate between
      // a padded and an unpadded height.
      const padding = Number.parseFloat(getComputedStyle(head).paddingBottom)
      const contentHeight = head.offsetHeight - padding
      setTail(Math.max(0, last.offsetHeight + lead - contentHeight))
    }
    const ro = new ResizeObserver(measure)
    ro.observe(head)
    ro.observe(last)
    window.addEventListener("resize", measure, { passive: true })
    measure()
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const shot = shotRef.current
    if (!canvas || !shot) return
    const ctx = canvas.getContext("2d")
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const frames: HTMLImageElement[] = []
    for (let i = 0; i < TUBE_FRAMES; i++) {
      const img = new Image()
      // Blob is another origin; without this the canvas taints and any
      // readback (tests, a future poster grab) throws.
      img.crossOrigin = "anonymous"
      img.src = `${TUBE}/tube-${String(i + 1).padStart(2, "0")}.webp?v=${TUBE_V}`
      frames.push(img)
    }

    let drawn = -1
    let sized = 0
    const draw = (i: number) => {
      const img = frames[i]
      if (!ctx || !img || !img.complete || img.naturalWidth === 0) return
      // The canvas is sized in percentages of the picture, so its buffer has
      // to follow the layout rather than a constant.
      const w = Math.round(canvas.clientWidth * Math.min(devicePixelRatio, 3))
      if (w > 0 && w !== sized) {
        canvas.width = w
        canvas.height = Math.round((w * TUBE_H) / TUBE_W)
        sized = w
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      drawn = i
    }

    /** The frame the tube's own picture has earned by where it is on screen. */
    const frameFor = () => {
      if (reduce) return TUBE_FRAMES - 1
      const vh = window.innerHeight
      const box = shot.getBoundingClientRect()
      const eye = (box.top + box.bottom) / 2 / vh
      const t = (SCRUB_FROM - eye) / (SCRUB_FROM - SCRUB_TO)
      return Math.round(Math.min(1, Math.max(0, t)) * (TUBE_FRAMES - 1))
    }

    /** The step the middle of the screen is on, for the marker. */
    const reading = () => {
      const eye = window.scrollY + window.innerHeight / 2
      let index = 0
      stepsRef.current.forEach((el, i) => {
        if (el && eye >= el.getBoundingClientRect().top + window.scrollY) {
          index = i
        }
      })
      return index
    }

    let shown = -1
    const render = () => {
      raf = 0
      const index = reading()
      // Only when it actually changes: this runs on every frame of every
      // scroll, and React does not need to hear the same number back.
      if (index !== shown) {
        shown = index
        setActive(index)
      }
      const f = frameFor()
      if (f !== drawn) draw(f)
    }

    let raf = 0
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(render)
    }
    // Late-loading frames: whichever one the scroll is on gets drawn as soon
    // as it lands, so the tube never shows an empty canvas.
    frames.forEach((img, i) => {
      img.onload = () => {
        if (frameFor() === i) draw(i)
      }
    })

    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule, { passive: true })
    schedule()
    return () => {
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  // 160 under the last step on desktop, not 96: the reviews' white started
  // right under the last card and the section read as cut off (Eugene,
  // 11.09). The reviews open with the same 160, so the seam between the two
  // grounds sits in the middle of the gap.
  return (
    <section className="bg-background py-10 md:pt-[48px] md:pb-[96px] lg:pb-[160px]">
      <PracticalStyles />
      <div
        className={`${section} grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-0`}
      >
        {/* Says its piece once and holds still while the steps go by. The
            offset is the axis (half a screen less 34) less the 71 between the
            block's top and the middle of the heading's first line: the
            eyebrow's 20, the 23 under it and half of the 56 line.

            It also starts where it parks relative to the steps: its first
            line level with the first step's title before either moves, so
            the two ride up as one and park together (Eugene, 16.09). The
            margin is the steps' lead-in less the same 29 head start; margin,
            not padding, so the sticky offset above still measures the
            block itself. */}
        <div
          ref={headRef}
          className="lg:sticky lg:top-[calc(50vh-105px)] lg:mt-[max(0px,calc(20vh-105px))] lg:self-start"
          style={{ paddingBottom: tail }}
        >
          <p className="type-label-lg text-neutral-500">{PRACTICAL.eyebrow}</p>
          {/* 54px in the file, between display-md and display-lg; the
              ladder's 48 keeps the three lines and the accent. */}
          <h2 className="type-display-md-emphasized mt-[23px] max-w-[532px] text-neutral-900">
            {PRACTICAL.heading.plain}
            <br />
            <span className="text-deep-green-600">
              {PRACTICAL.heading.accent}
            </span>
          </h2>
          {/* The page's longest run without a way to book: three screens of
              steps between the baseline's button and the close (Eugene,
              11.09). It sits in the sticky column, so it rides with the
              heading for the whole run. Below the h2, so the first line the
              steps park on does not move; the tail padding is measured
              from the content and absorbs the extra height. Same label as
              the navbar's button. */}
          <BookingCta
            productId={PRODUCT_IDS.bloodTest}
            step="product"
            overrideHref={bookingFlowUrl()}
            trackingLabel="home_v3_practical_cta"
            trackingLocation="dev/home-v4"
            className="mt-8 block w-full sm:inline-block sm:w-auto lg:mt-10"
          >
            <Button
              size="lg"
              variant="primary"
              className="group w-full bg-neutral-950 focus-visible:ring-neutral-400 sm:w-auto"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              {PRACTICAL.cta}
            </Button>
          </BookingCta>
        </div>

        {/* A short lead-in brings the first step into view alongside the
            heading, which starts on the same line (see its margin). Equal
            columns without a gap put the rail at page center. */}
        <div className="lg:pt-[max(29px,calc(20vh-76px))]">
          <div className="relative flex flex-col gap-16 lg:gap-24">
            <div className="hv3-pr-rail absolute top-0 bottom-0 left-0 hidden w-px bg-neutral-100 lg:block" />

            {steps.map((step, i) => (
              <div
                key={step.label}
                ref={(el) => {
                  stepsRef.current[i] = el
                }}
                className={cn(
                  "relative lg:min-h-[max(52vh,440px)]",
                  // The first step parks longer: the reader is still on the
                  // heading when it arrives, so it gets an extra 24vh of
                  // travel before the second step pushes it out.
                  i === 0 && "lg:min-h-[max(76vh,620px)]",
                  // The last step has no spare runway. The instant its marker
                  // reaches the heading's first line, the shared bottom edge
                  // releases both sticky blocks and they leave together.
                  i === steps.length - 1 && "lg:min-h-0"
                )}
              >
                <div
                  ref={i === steps.length - 1 ? lastRef : undefined}
                  className="flex flex-col gap-2.5 lg:sticky lg:top-[calc(50vh-76px)] lg:pl-16"
                >
                  {/* The cadence itself: Day 1 · Day 5–7 · Month 3–6 · Every
                      12 months. Over the title, not under it: under it the
                      small grey line read as the title's footnote and the
                      hierarchy fell apart (Eugene, 18.09). */}
                  <p className="type-label-md text-neutral-500">
                    {step.figure}
                  </p>
                  {/* The marker rides the rail, on the middle of the title's first
                  line — hence the lh, which is why the line height is spelled
                  out on the wrapper. */}
                  <div
                    className="relative"
                    style={{ lineHeight: "var(--leading-headline-sm)" }}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "ring-background absolute left-[-67px] hidden size-[8px] ring-4 transition-colors duration-300 lg:block",
                        i === active ? "bg-neutral-900" : "bg-neutral-300"
                      )}
                      style={{ top: "calc(0.5lh - 4px)" }}
                    />
                    <h3 className="type-headline-sm text-neutral-900">
                      {step.label}
                    </h3>
                  </div>

                  <p className="type-body-lg max-w-[360px] text-neutral-600">
                    {step.body}
                  </p>

                  {/* Process imagery, reusing the membership plan asset.
                      Re-ordered for the rhythm (18.09): the blood tubes on
                      day 1, the report in hand on day 5–7, the booking screen
                      for the optional follow-up, the trend for the next
                      check-up. */}
                  <div
                    ref={i === 0 ? shotRef : undefined}
                    className="relative mt-1 aspect-[3/2] w-full max-w-[360px] overflow-hidden rounded-[16px] bg-neutral-100"
                  >
                    {i === 0 ? (
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(180deg, #cdd0d5 0%, #637983 100%)",
                        }}
                      >
                        <NextImage
                          src={blobMediaUrl(
                            "/images/marketing/home-v4/practical/blood-landscape-color.webp"
                          )}
                          alt="Two cropped Ahead blood sample tubes with purple and blue caps beside blood test categories"
                          fill
                          sizes="(min-width: 1024px) 360px, 100vw"
                          className="object-cover"
                        />
                      </div>
                    ) : i === 1 ? (
                      <NextImage
                        src={blobMediaUrl(
                          "/images/marketing/home-v3/practical-gray-botanical/03-report-hand-green.webp"
                        )}
                        alt="A hand holding a phone displaying the Ahead health report with a green biological age tile against a softly blurred landscape"
                        fill
                        sizes="(min-width: 1024px) 360px, 100vw"
                        className="object-cover"
                      />
                    ) : i === 2 ? (
                      <NextImage
                        src={blobMediaUrl(
                          "/images/marketing/home-v3/practical-gray-botanical/01-book.webp"
                        )}
                        alt="Appointment booking in a mobile browser with a confirmation at Hirslanden"
                        fill
                        sizes="(min-width: 1024px) 360px, 100vw"
                        className="object-cover"
                      />
                    ) : i === 3 ? (
                      // A square render, not a photograph: cropped to 3/2 it
                      // blew the card up past the frame (Eugene, 18.09). It
                      // sits whole now, flush left so the trend line still
                      // comes in from the edge, fading into a matching pale background.
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(135deg, #e9f0f4 0%, #f3f2ed 100%)",
                        }}
                      >
                        <NextImage
                          src={blobMediaUrl(
                            "/images/marketing/home-v4/practical/tracking-soft-glass.webp"
                          )}
                          alt="A biomarker trend chart on a glass card"
                          fill
                          sizes="(min-width: 1024px) 240px, 70vw"
                          className="object-contain object-left"
                          style={{
                            maskImage:
                              "linear-gradient(to right, black 0%, black 55%, transparent 68%)",
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * The wireframe's numbered list (Figma 328:13967), parked while the deck
 * above is tried in its place. The hairline is the list's own left border;
 * each step hangs its dot on it.
 */
export function HomeV4PracticalSteps() {
  const { PRACTICAL } = useHomeV4Copy()
  return (
    <ol className="relative flex max-w-[545px] flex-col gap-[38px] border-l border-neutral-900/18 pl-8 lg:mt-[43px] lg:ml-[52px]">
      {PRACTICAL.steps.map((step) => (
        <li key={step.label} className="relative">
          <span
            className={cn(
              "absolute top-[3px] -left-[38.5px] size-3 rounded-full",
              step.optional
                ? "border-[1.5px] border-dashed border-neutral-900 bg-neutral-100"
                : "bg-neutral-900"
            )}
            aria-hidden
          />
          <p className="type-label-md text-neutral-500">{step.label}</p>
          <p
            className={cn(
              "type-display-sm mt-2.5",
              step.optional ? "text-neutral-600" : "text-neutral-900"
            )}
          >
            {step.figure}
          </p>
          <p className="type-body-md mt-2.5 max-w-[305px] text-neutral-600">
            {step.body}
          </p>
        </li>
      ))}
    </ol>
  )
}
